import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { BookOpen, FileText, Calculator, BookA, ArrowRight } from "lucide-react";
import { InternalLinksHub } from "@/components/internal-links-hub";
import { EabBannerBlock } from "@/components/eab-banner";

export const Route = createFileRoute("/hr-resources/")({
  head: () => ({
    meta: [
      { title: "HR Resources, Guides, Templates & Calculators" },
      {
        name: "description",
        content:
          "Free UK HR resources: 500+ templates, employment law guides, statutory pay calculators and a 100-term glossary. All updated for 2026.",
      },
      { property: "og:title", content: "HR Resources, Guides, Templates & Calculators" },
      { property: "og:description", content: "Free UK HR templates, calculators, guides and glossary, all updated for 2026." },
      { property: "og:url", content: "https://www.hrservicecentre.co.uk/hr-resources" },
    ],
    links: [{ rel: "canonical", href: "https://www.hrservicecentre.co.uk/hr-resources" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "HR Resources, Guides, Templates & Calculators",
          url: "https://www.hrservicecentre.co.uk/hr-resources",
          description:
            "Free UK HR resources: templates, employment law guides, statutory pay calculators and a glossary, all updated for 2026.",
          isPartOf: { "@id": "https://www.hrservicecentre.co.uk/#website" },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Blog & guides", url: "https://www.hrservicecentre.co.uk/hr-insights" },
              { "@type": "ListItem", position: 2, name: "Free templates", url: "https://www.hrservicecentre.co.uk/hr-templates" },
              { "@type": "ListItem", position: 3, name: "HR calculators", url: "https://www.hrservicecentre.co.uk/hr-calculators" },
              { "@type": "ListItem", position: 4, name: "Glossary", url: "https://www.hrservicecentre.co.uk/hr-glossary" },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hrservicecentre.co.uk/" },
            { "@type": "ListItem", position: 2, name: "Resources", item: "https://www.hrservicecentre.co.uk/hr-resources" },
          ],
        }),
      },
    ],
  }),
  component: ResourcesHub,
});

const tiles = [
  {
    to: "/hr-insights" as const,
    icon: BookOpen,
    title: "Blog & guides",
    body: "Practical, plain-English guidance on UK employment law and HR best practice.",
  },
  {
    to: "/hr-templates" as const,
    icon: FileText,
    title: "Free templates",
    body: "Solicitor-drafted contracts, letters, policies and checklists. Free to download.",
  },
  {
    to: "/hr-calculators" as const,
    icon: Calculator,
    title: "HR calculators",
    body: "Statutory redundancy, holiday entitlement, SSP and more, using 2026/27 rates.",
  },
  {
    to: "/hr-glossary" as const,
    icon: BookA,
    title: "Glossary",
    body: "Plain-English definitions for 100+ UK employment law and HR terms.",
  },
] as const;

function ResourcesHub() {
  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Resources", path: "/hr-resources" },
        ]}
        eyebrow="Resources"
        title="Free HR & Employment Law Resources for UK Employers"
        subtitle="Templates, guides, calculators and articles, free HR and employment law resources."
        intro="Everything you need to run your people function properly, written by qualified UK HR advisors and employment solicitors."
      />

      <section className="px-6 py-20 lg:px-8">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                className="group flex items-start gap-6 rounded-2xl bg-card p-8 ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:ring-accent/30"
              >
                <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
                  <Icon className="size-6" />
                </span>
                <div>
                  <h2 className="font-serif text-xl text-brand">{t.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent">
                    Browse <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
      <EabBannerBlock membership source="resources_hub" />

      <InternalLinksHub heading="Pair our resources with senior HR & employment law support" />
    </>
  );
}
